import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Layers,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Zap,
  GripVertical,
  CheckCircle,
  Circle,
  AlertCircle,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";
import LibraryPicker from "./LibraryPicker";

const TYPE_OPTIONS = [
  { value: "canvas", label: "Canvas" },
  { value: "wall_mural", label: "Wall Mural" },
];

const SOURCE_OPTIONS = [
  "mockup_template",
  "detail_crop",
  "room_scene",
  "artwork",
  "manual_upload",
  "generated_from_artwork",
];

const ROLE_OPTIONS = ["hero", "gallery", "detail", "thumbnail"];
const RATIO_OPTIONS = ["4:5", "1:1", "16:9", "wall-wide", "3:2", "original"];

const EMPTY_SLOT = {
  key: "",
  label: "",
  description: "",
  order: 1,
  required: false,
  source_type: "manual_upload",
  aspect_ratio: "4:5",
  storefront_role: "gallery",
  allow_multiple: false,
  notes: "",
  mockup_config: { background_url: "", placement: { x: 0.15, y: 0.15, w: 0.70, h: 0.70 } },
};

function PackSlotPreview({ slots, packSlug }) {
  const sortedSlots = useMemo(
    () => [...slots].sort((a, b) => a.order - b.order),
    [slots]
  );
  return (
    <div className="border-t border-white/[0.04] p-4 bg-white/[0.01]">
      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-3">Slot Preview</p>
      <div className="space-y-1.5">
        {sortedSlots.map((slot, idx) => (
          <div key={slot.key} className="flex items-center gap-3 text-[10px]">
            <span className="text-white/20 w-4">{idx + 1}.</span>
            {slot.required
              ? <AlertCircle size={11} className="text-red-400 shrink-0" />
              : <Circle size={11} className="text-white/15 shrink-0" />
            }
            <span className="font-medium text-white/70">{slot.label}</span>
            <span className="text-white/25 font-mono">{slot.key}</span>
            <span className="ml-auto text-white/20">{slot.aspect_ratio}</span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${slot.storefront_role === "hero" ? "bg-[#CBA052]/15 text-[#CBA052]" : "bg-white/5 text-white/30"}`}>
              {slot.storefront_role}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-white/[0.04] flex items-center gap-3 flex-wrap">
        <p className="text-[9px] text-white/25">Storefront rules: hero slot → collection card + PDP hero | fallback → first filled slot</p>
        <code className="text-[9px] text-white/20 font-mono">{packSlug}</code>
      </div>
    </div>
  );
}


const EMPTY_PACK = {
  name: "",
  slug: "",
  product_type: "canvas",
  description: "",
  active: true,
  default_pack: false,
  slots: [],
};

function SlotRow({ slot, index, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, productType }) {
  const [expanded, setExpanded] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const mc = slot.mockup_config || { background_url: "", placement: { x: 0.15, y: 0.15, w: 0.70, h: 0.70 } };
  const placement = mc.placement || { x: 0.15, y: 0.15, w: 0.70, h: 0.70 };

  const updateMockupConfig = (field, value) => {
    onChange("mockup_config", { ...mc, [field]: value });
  };
  const updatePlacement = (field, raw) => {
    const val = parseFloat(raw);
    const clamped = isNaN(val) ? 0 : Math.min(1, Math.max(0, val));
    onChange("mockup_config", { ...mc, placement: { ...placement, [field]: clamped } });
  };

  return (
    <div className="border border-white/[0.06] rounded bg-white/[0.01]" data-testid={`slot-row-${index}`}>
      <div className="flex items-center gap-2 p-2.5">
        <div className="flex flex-col gap-0.5 shrink-0">
          <button onClick={onMoveUp} disabled={isFirst} className="text-white/20 hover:text-white/60 disabled:opacity-20"><ChevronUp size={12} /></button>
          <button onClick={onMoveDown} disabled={isLast} className="text-white/20 hover:text-white/60 disabled:opacity-20"><ChevronDown size={12} /></button>
        </div>
        <span className="text-[10px] text-white/20 w-4 shrink-0">{index + 1}.</span>
        <input
          value={slot.key}
          onChange={(e) => onChange("key", e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""))}
          placeholder="slot_key"
          className="w-28 bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2 py-1 outline-none font-mono"
        />
        <input
          value={slot.label}
          onChange={(e) => onChange("label", e.target.value)}
          placeholder="Display Label"
          className="flex-1 bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2 py-1 outline-none"
        />
        <label className="flex items-center gap-1 cursor-pointer shrink-0">
          <input type="checkbox" checked={slot.required} onChange={(e) => onChange("required", e.target.checked)} className="accent-[#CBA052]" />
          <span className="text-[9px] text-white/40">Req</span>
        </label>
        <button onClick={() => setExpanded((v) => !v)} className="text-white/25 hover:text-white/60 shrink-0">
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <button onClick={onRemove} className="text-white/20 hover:text-red-400 shrink-0"><X size={13} /></button>
      </div>

      {expanded && (
        <div className="px-3 pb-3 border-t border-white/[0.04] pt-3 space-y-3">
          {/* Core slot settings */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[9px] text-white/40 uppercase block mb-1">Source Type</span>
              <AdminSelect value={slot.source_type} onChange={(e) => onChange("source_type", e.target.value)} size="sm">
                {SOURCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </AdminSelect>
            </label>
            <label className="block">
              <span className="text-[9px] text-white/40 uppercase block mb-1">Aspect Ratio</span>
              <AdminSelect value={slot.aspect_ratio} onChange={(e) => onChange("aspect_ratio", e.target.value)} size="sm">
                {RATIO_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </AdminSelect>
            </label>
            <label className="block">
              <span className="text-[9px] text-white/40 uppercase block mb-1">Storefront Role</span>
              <AdminSelect value={slot.storefront_role} onChange={(e) => onChange("storefront_role", e.target.value)} size="sm">
                {ROLE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </AdminSelect>
            </label>
            <label className="block md:col-span-2">
              <span className="text-[9px] text-white/40 uppercase block mb-1">Description</span>
              <input value={slot.description} onChange={(e) => onChange("description", e.target.value)} className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2 py-1.5 outline-none" />
            </label>
            <label className="block">
              <span className="text-[9px] text-white/40 uppercase block mb-1">Notes</span>
              <input value={slot.notes} onChange={(e) => onChange("notes", e.target.value)} className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2 py-1.5 outline-none" />
            </label>
          </div>

          {/* Mockup Config — shown for mockup_template and detail_crop slots */}
          {slot.source_type === "mockup_template" && (
            <div className="border border-[#CBA052]/20 rounded p-2.5 bg-[#CBA052]/5" data-testid={`mockup-config-${index}`}>
              <p className="text-[9px] text-[#CBA052]/80 uppercase tracking-wider font-bold mb-2">Auto-Generate Config (Mockup Template)</p>
              <label className="block mb-2">
                <span className="text-[9px] text-white/40 uppercase block mb-1">Background Mockup URL</span>
                <input
                  value={mc.background_url}
                  onChange={(e) => updateMockupConfig("background_url", e.target.value)}
                  placeholder="https://... or /api/admin/uploads/..."
                  className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2 py-1.5 outline-none focus:border-[#CBA052]/40"
                  data-testid={`mockup-bg-url-${index}`}
                />
                <span className="text-[9px] text-white/25 mt-1 block">The framed wall / room background image. Artwork will be placed inside this.</span>
                <button
                  type="button"
                  onClick={() => setShowLibrary((v) => !v)}
                  className="mt-1.5 text-[9px] font-bold text-[#CBA052]/70 hover:text-[#CBA052] transition-colors"
                  data-testid={`mockup-pick-library-${index}`}
                >
                  {showLibrary ? "Close Library" : "Pick from Library"}
                </button>
                {showLibrary && (
                  <LibraryPicker
                    productType={productType}
                    onSelect={(item) => {
                      updateMockupConfig("background_url", item.image_url);
                      if (item.placement_defaults) {
                        onChange("mockup_config", { ...mc, background_url: item.image_url, placement: { ...placement, ...item.placement_defaults } });
                      }
                      setShowLibrary(false);
                    }}
                    onClose={() => setShowLibrary(false)}
                  />
                )}
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { field: "x", label: "Left (X)" },
                  { field: "y", label: "Top (Y)" },
                  { field: "w", label: "Width (W)" },
                  { field: "h", label: "Height (H)" },
                ].map(({ field, label }) => (
                  <label key={field} className="block">
                    <span className="text-[9px] text-white/40 uppercase block mb-1">{label}</span>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={placement[field]}
                      onChange={(e) => updatePlacement(field, e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2 py-1.5 outline-none font-mono focus:border-[#CBA052]/40"
                      data-testid={`placement-${field}-${index}`}
                    />
                  </label>
                ))}
              </div>
              <p className="text-[9px] text-white/20 mt-2">Placement values are 0.0–1.0 fractions of the background image size. E.g. x=0.15, y=0.10, w=0.70, h=0.80 places artwork in the central 70%×80% area.</p>
            </div>
          )}

          {slot.source_type === "detail_crop" && (
            <div className="border border-purple-500/20 rounded p-2.5 bg-purple-500/5">
              <p className="text-[9px] text-purple-400/80 uppercase tracking-wider font-bold mb-1">Auto-Generate Config (Detail Crop)</p>
              <p className="text-[10px] text-white/40">Automatically crops the base artwork to the slot's aspect ratio ({slot.aspect_ratio}) using a centred crop. No extra configuration needed.</p>
            </div>
          )}

          {slot.source_type === "artwork" && (
            <div className="border border-blue-500/20 rounded p-2.5 bg-blue-500/5">
              <p className="text-[9px] text-blue-400/80 uppercase tracking-wider font-bold mb-1">Auto-Generate Config (Artwork)</p>
              <p className="text-[10px] text-white/40">Uses the base artwork directly (scaled to max 2400px). No extra configuration needed.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PackForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || { ...EMPTY_PACK });

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addSlot = () => {
    setForm((f) => ({
      ...f,
      slots: [...f.slots, { ...EMPTY_SLOT, order: f.slots.length + 1 }],
    }));
  };

  const updateSlot = (idx, field, value) => {
    setForm((f) => {
      const slots = [...f.slots];
      slots[idx] = { ...slots[idx], [field]: value };
      return { ...f, slots };
    });
  };

  const removeSlot = (idx) => {
    setForm((f) => ({ ...f, slots: f.slots.filter((_, i) => i !== idx) }));
  };

  const moveSlot = (idx, dir) => {
    setForm((f) => {
      const slots = [...f.slots];
      const target = idx + dir;
      if (target < 0 || target >= slots.length) return f;
      [slots[idx], slots[target]] = [slots[target], slots[idx]];
      return { ...f, slots };
    });
  };

  return (
    <div className="border border-[#CBA052]/30 bg-[#0a0a0a] rounded p-5 mb-5" data-testid="pack-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <label className="block">
          <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Pack Name</span>
          <input value={form.name} onChange={(e) => updateField("name", e.target.value)} className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" data-testid="pack-name-input" />
        </label>
        <label className="block">
          <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Product Type</span>
          <AdminSelect value={form.product_type} onChange={(e) => updateField("product_type", e.target.value)} size="md" data-testid="pack-type-select">
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </AdminSelect>
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-1">Description</span>
          <input value={form.description} onChange={(e) => updateField("description", e.target.value)} className="w-full bg-white/[0.04] border border-white/10 text-white text-xs rounded px-3 py-2 outline-none" data-testid="pack-description-input" />
        </label>
      </div>

      <div className="flex items-center gap-5 mb-4">
        <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={(e) => updateField("active", e.target.checked)} className="accent-[#CBA052]" />Active
        </label>
        <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
          <input type="checkbox" checked={form.default_pack} onChange={(e) => updateField("default_pack", e.target.checked)} className="accent-[#CBA052]" />Default pack for this type
        </label>
      </div>

      {/* Slots */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-white/50 uppercase tracking-wider">Gallery Slots ({form.slots.length})</span>
          <button onClick={addSlot} className="flex items-center gap-1 text-[10px] text-[#CBA052] border border-[#CBA052]/30 px-2.5 py-1 rounded hover:bg-[#CBA052]/10 transition-colors" data-testid="add-slot-btn">
            <Plus size={10} /> Add Slot
          </button>
        </div>
        {form.slots.length === 0 && (
          <p className="text-[10px] text-white/25 text-center py-4 border border-dashed border-white/10 rounded">No slots yet — click Add Slot</p>
        )}
        <div className="space-y-2">
          {form.slots.map((slot, idx) => (
            <SlotRow
              key={slot.key || `slot-${idx}`}
              slot={slot}
              index={idx}
              onChange={(field, val) => updateSlot(idx, field, val)}
              onRemove={() => removeSlot(idx)}
              onMoveUp={() => moveSlot(idx, -1)}
              onMoveDown={() => moveSlot(idx, 1)}
              isFirst={idx === 0}
              isLast={idx === form.slots.length - 1}
              productType={form.product_type}
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-xs text-white/30 border border-white/10 px-4 py-2 rounded hover:text-white/60">Cancel</button>
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.name.trim()}
          className="text-xs font-bold text-black bg-[#CBA052] px-4 py-2 rounded hover:opacity-90 disabled:opacity-50"
          data-testid="pack-save-btn"
        >
          {saving ? "Saving..." : "Save Pack"}
        </button>
      </div>
    </div>
  );
}

export default function AdminTemplatePacks() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeType, setActiveType] = useState("canvas");
  const [expandedPack, setExpandedPack] = useState(null);

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/api/admin/templates/packs");
      setPacks(res.data.packs || []);
    } catch (err) { console.error("[AdminTemplatePacks] fetch failed:", err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const res = await adminApi.post("/api/admin/templates/packs/seed");
      setSeedResult(res.data);
      fetchPacks();
    } catch (err) {
      setSeedResult({ message: err.response?.data?.detail || "Seed failed" });
    }
    setSeeding(false);
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editingPack) {
        await adminApi.put(`/api/admin/templates/packs/${editingPack.id}`, form);
      } else {
        await adminApi.post("/api/admin/templates/packs", form);
      }
      setShowForm(false);
      setEditingPack(null);
      fetchPacks();
    } catch (err) { console.error("[AdminTemplatePacks] save failed:", err); }
    setSaving(false);
  };

  const handleDelete = async (pack) => {
    if (!window.confirm(`Delete pack "${pack.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/api/admin/templates/packs/${pack.id}`);
      fetchPacks();
    } catch (err) { console.error("[AdminTemplatePacks] delete pack failed:", err); }
  };

  const openEdit = (pack) => {
    setEditingPack(pack);
    setShowForm(true);
  };

  const openNew = () => {
    setEditingPack(null);
    setShowForm(true);
  };

  const filteredPacks = packs.filter((p) => p.product_type === activeType);

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="admin-template-packs">
      {/* Header */}
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
        <Layers size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Presentation Template Packs</h3>
        <span className="text-[10px] text-white/30">{packs.length} packs</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="flex items-center gap-1.5 text-[10px] font-bold text-white/50 border border-white/10 px-3 py-1.5 rounded hover:text-white hover:border-white/25 disabled:opacity-50 transition-colors"
            data-testid="seed-packs-btn"
          >
            <Zap size={11} />
            {seeding ? "Seeding..." : "Seed Default Packs"}
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90"
            data-testid="new-pack-btn"
          >
            <Plus size={12} /> New Pack
          </button>
        </div>
      </div>

      <div className="p-5">
        {/* Seed result */}
        {seedResult && (
          <div className={`mb-4 p-3 rounded text-xs border ${seedResult.created ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"}`} data-testid="seed-result">
            {seedResult.created?.length > 0
              ? `Created: ${seedResult.created.join(", ")}${seedResult.skipped?.length ? ` | Already existed: ${seedResult.skipped.join(", ")}` : ""}`
              : seedResult.skipped?.length > 0
                ? `All packs already existed: ${seedResult.skipped.join(", ")}`
                : seedResult.message}
          </div>
        )}

        {/* Pack Form */}
        {showForm && (
          <PackForm
            initial={editingPack ? { ...editingPack } : { ...EMPTY_PACK, product_type: activeType }}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingPack(null); }}
            saving={saving}
          />
        )}

        {/* Type tabs */}
        <div className="flex gap-1 mb-5">
          {TYPE_OPTIONS.map((t) => (
            <button
              key={t.value}
              onClick={() => setActiveType(t.value)}
              className={`text-xs font-bold px-4 py-2 rounded transition-colors ${activeType === t.value ? "bg-white text-black" : "text-white/40 hover:text-white border border-white/10"}`}
              data-testid={`type-tab-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Pack list */}
        {loading ? (
          <div className="text-xs text-white/30 text-center py-8">Loading packs...</div>
        ) : filteredPacks.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-white/10 rounded" data-testid="packs-empty">
            <Layers size={20} className="text-white/10 mx-auto mb-3" />
            <p className="text-xs text-white/30 mb-2">No packs for {activeType === "canvas" ? "Canvas" : "Wall Mural"} yet.</p>
            <p className="text-[10px] text-white/20">Click "Seed Default Packs" to get started, or create one manually.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="packs-list">
            {filteredPacks.map((pack) => {
              const isExpanded = expandedPack === pack.id;
              const requiredCount = pack.slots?.filter((s) => s.required).length || 0;
              return (
                <div key={pack.id} className="border border-white/[0.06] rounded overflow-hidden" data-testid={`pack-row-${pack.id}`}>
                  {/* Pack header */}
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/[0.02]"
                    onClick={() => setExpandedPack(isExpanded ? null : pack.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-white">{pack.name}</span>
                        {pack.default_pack && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-[#CBA052]/15 text-[#CBA052] border-[#CBA052]/30">Default</span>
                        )}
                        {!pack.active && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20">Inactive</span>
                        )}
                        {pack.active && !pack.default_pack && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border bg-green-500/10 text-green-400 border-green-500/20">Active</span>
                        )}
                      </div>
                      <p className="text-[10px] text-white/30 mt-0.5">{pack.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-white/40">{pack.slots?.length || 0} slots</p>
                      <p className="text-[9px] text-red-400/60">{requiredCount} required</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(pack); }} className="p-1.5 bg-white/5 rounded hover:bg-white/10" title="Edit">
                        <Pencil size={11} className="text-white/40" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(pack); }} className="p-1.5 bg-white/5 rounded hover:bg-red-500/20" title="Delete">
                        <Trash2 size={11} className="text-white/40" />
                      </button>
                    </div>
                    {isExpanded ? <ChevronUp size={14} className="text-white/20 shrink-0" /> : <ChevronDown size={14} className="text-white/20 shrink-0" />}
                  </div>

                  {/* Slot preview */}
                  {isExpanded && pack.slots?.length > 0 && (
                    <PackSlotPreview slots={pack.slots} packSlug={pack.slug} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
